"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs/Observable';
import { Auth } from '../auth/auth.service';
import { ErrorTracking } from '../tracking';
import { ApiHttp } from './api-http.service';
import { isDefined, objectFilterByKeys, pathListEqual, pathToPathList, referenceToReferenceList, sameElements, setDiff, } from './helpers';
import { ListObservable } from './list-observable';
import { ObjectObservable } from './object-observable';
import { WebSocketService } from './websocket';
var Datastore = (function () {
    function Datastore(store, apiHttp, auth, webSocketService, errorTracking) {
        this.store = store;
        this.apiHttp = apiHttp;
        this.auth = auth;
        this.webSocketService = webSocketService;
        this.errorTracking = errorTracking;
    }
    Datastore.prototype.list = function (arg1) {
        var _this = this;
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        var optionsQuery = {};
        if (args.length && args[args.length - 1].query) {
            optionsQuery = args.pop().query;
        }
        var args$ = [arg1].concat(args).map(function (key) { return (key instanceof Observable ? key : Observable.of(key)); });
        var pathStream = Observable.combineLatest(args$);
        var queryStream = Observable.combineLatest([
            optionsQuery.limit instanceof Observable
                ? optionsQuery.limit
                : Observable.of(optionsQuery.limit),
            optionsQuery.offset instanceof Observable
                ? optionsQuery.offset
                : Observable.of(optionsQuery.offset),
        ], function (limit, offset) { return ({ limit: limit, offset: offset }); });
        var refStream = Observable.combineLatest([pathStream, queryStream], function (path, query) { return ({ path: path, query: query }); })
            .do(function (ref) {
            var action = {
                type: 'REQUEST_DATA',
                payload: {
                    type: ref.path[0],
                    refList: referenceToReferenceList(ref),
                },
            };
            _this.store.dispatch(action);
        })
            .shareReplay(1);
        var fetchStream = this.apiHttp.requestStream
            .withLatestFrom(refStream)
            .filter(function (_a) {
            var fetchReference = _a[0], originalReference = _a[1];
            return pathListEqual(fetchReference.pathList, pathToPathList(originalReference.path));
        })
            .map(function (_a) {
            var fetchReference = _a[0], originalReference = _a[1];
            return originalReference;
        });
        var websocketStream = this.webSocketService.fromServerStream.combineLatest(refStream, function (ws, ref) { return ref; });
        return new ListObservable(refStream, this.apiHttp, this.auth, this.store, this.errorTracking, Observable.merge(refStream, fetchStream, websocketStream)
            .switchMap(function (_a) {
            var path = _a.path;
            return (_b = _this.store).pluck.apply(_b, path);
            var _b;
        })
            .combineLatest(queryStream)
            .distinctUntilChanged(function (_a, _b) {
            var prevData = _a[0];
            var currData = _b[0], currQuery = _b[1];
            return prevData === currData ||
                // TODO: handle different lists
                (prevData !== undefined &&
                    currData !== undefined &&
                    prevData.lists.default.length === currData.lists.default.length &&
                    !currData.lists.default
                        .slice(currQuery.offset || 0)
                        .some(function (key) {
                        return prevData.entities[key] === undefined ||
                            currData.entities[key] === undefined ||
                            prevData.entities[key].entity !==
                                currData.entities[key].entity;
                    }));
        })
            .map(function (_a) {
            var data = _a[0], query = _a[1];
            if (data && data.lists && data.lists.default) {
                return data.lists.default
                    .slice(query.offset || 0)
                    .map(function (id) { return data.entities[id].entity; });
            }
            return [];
        }));
    };
    Datastore.prototype.object = function (arg1) {
        var _this = this;
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        var args$ = [arg1].concat(args).map(function (key) { return (key instanceof Observable ? key : Observable.of(key)); });
        var refStream = Observable.combineLatest(args$)
            .map(function (path) { return ({ path: path }); })
            .switchMap(function (_a) {
            var path = _a.path;
            return (_b = _this.store).pluck.apply(_b, path).take(1);
            var _b;
        }, function (ref, data) { return ({ ref: ref, data: data }); })
            .do(function (_a) {
            var ref = _a.ref, data = _a.data;
            // Don't fetch if this was fetched in the last second.
            if (!data || Number(data.timeFetched) + 1000 < Date.now()) {
                var action = {
                    type: 'REQUEST_DATA',
                    payload: {
                        type: ref.path[0],
                        refList: referenceToReferenceList(ref),
                    },
                };
                _this.store.dispatch(action);
            }
        })
            .map(function (_a) {
            var ref = _a.ref;
            return ref;
        })
            .shareReplay(1);
        var fetchStream = this.apiHttp.requestStream
            .withLatestFrom(refStream)
            .filter(function (_a) {
            var fetchReference = _a[0], originalReference = _a[1];
            return pathListEqual(fetchReference.pathList, pathToPathList(originalReference.path));
        })
            .map(function (_a) {
            var fetchReference = _a[0], originalReference = _a[1];
            return originalReference;
        });
        var subscriptionStream = arg1 === 'onlineOffline'
            ? this.webSocketService
                .subscribeOnlineOffline(args$[1])
                .combineLatest(refStream, function (ss, ref) { return ref; })
            : refStream;
        var websocketStream = this.webSocketService.fromServerStream.combineLatest(refStream, function (ws, ref) { return ref; });
        return new ObjectObservable(refStream, this.apiHttp, this.store, Observable.merge(refStream, fetchStream, subscriptionStream, websocketStream)
            .switchMap(function (_a) {
            var path = _a.path;
            return (_b = _this.store).pluck.apply(_b, path);
            var _b;
        })
            .filter(isDefined)
            .map(function (obj) { return obj.entity; })
            .distinctUntilChanged());
    };
    /* Returns a list of objects at a location.
     * If an observable is passed in for key2 then only the delta will be
     * refetched from the network.
     */
    Datastore.prototype.objects = function (key1, key2) {
        var _this = this;
        var args$ = [key1, key2].map(function (key) { return (key instanceof Observable ? key : Observable.of(key)); });
        var refStream = Observable.combineLatest(args$)
            .startWith([key1, []])
            .map(function (pathList) { return ({ refList: { pathList: pathList } }); })
            .pairwise() // Use pairwise and only fetch new things
            .do(function (_a) {
            var refList1 = _a[0].refList, refList2 = _a[1].refList;
            var newIds = setDiff(refList2.pathList[1], refList1.pathList[1]);
            if (newIds.length) {
                var type = refList2.pathList[0];
                var action = {
                    type: 'REQUEST_DATA',
                    payload: { type: type, refList: { pathList: [type, newIds] } },
                };
                _this.store.dispatch(action);
            }
        })
            .map(function (_a) {
            var refList1 = _a[0].refList, refList2 = _a[1].refList;
            return refList2;
        })
            .shareReplay(1);
        var fetchStream = this.apiHttp.requestStream
            .withLatestFrom(refStream)
            .filter(function (_a) {
            var fetchReference = _a[0], originalReference = _a[1];
            return pathListEqual(fetchReference.pathList, originalReference.pathList);
        })
            .map(function (_a) {
            var fetchReference = _a[0], originalReference = _a[1];
            return originalReference;
        });
        var websocketStream = this.webSocketService.fromServerStream.combineLatest(refStream, function (ws, ref) { return ref; });
        return Observable.merge(refStream, fetchStream, websocketStream).switchMap(function (_a) {
            var pathList = _a.pathList;
            return _this.store
                .pluck(pathList[0])
                .filter(isDefined)
                .map(function (rawEntitiesObject) {
                return objectFilterByKeys(rawEntitiesObject, pathList[1]);
            })
                .map(function (rawEntitiesObject) { return Object.values(rawEntitiesObject); })
                .map(function (rawEntities) { return rawEntities.map(function (rawEntity) { return rawEntity.entity; }); })
                .distinctUntilChanged(sameElements);
        });
    };
    Datastore = __decorate([
        Injectable(),
        __metadata("design:paramtypes", [Store,
            ApiHttp,
            Auth,
            WebSocketService,
            ErrorTracking])
    ], Datastore);
    return Datastore;
}());
export { Datastore };



// WEBPACK FOOTER //
// datastore.js